import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Check, ArrowRight, ArrowLeft, Loader2, Wand2 } from "lucide-react";
import { useLang, tx } from "@/context/LangContext";
import { FORM_BUDGETS, FORM_TIMELINES, FORM_TYPES, T } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const Chip = ({ active, onClick, children, testid }) => (
    <button
        data-testid={testid}
        onClick={onClick}
        className={`rounded-full border px-4 py-2.5 font-mono text-[10px] tracking-[0.2em] transition-all duration-300 md:text-[11px] ${
            active
                ? "border-violet bg-violet/15 text-cream"
                : "border-cream/25 text-cream/60 hover:border-cream/60 hover:text-cream"
        }`}
    >
        {children}
    </button>
);

const Field = ({ label, error, testid, children }) => (
    <label className="block">
        <span className="font-mono text-[10px] tracking-[0.3em] text-cream/50">
            {label}
        </span>
        {children}
        {error && (
            <span
                data-testid={`${testid}-error`}
                className="mt-2 block font-mono text-[10px] tracking-[0.2em] text-blood"
            >
                {error}
            </span>
        )}
    </label>
);

export default function ContactForm() {
    const { lang } = useLang();
    const [step, setStep] = useState(0);
    const [dir, setDir] = useState(1);
    const [data, setData] = useState({
        type: "",
        budget: "",
        timeline: "",
        name: "",
        email: "",
        company: "",
        message: "",
    });
    const [errors, setErrors] = useState({});
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);
    const [enhancing, setEnhancing] = useState(false);

    const t = T.form;
    const set = (k) => (v) => {
        setData((d) => ({ ...d, [k]: v }));
        setErrors((e) => ({ ...e, [k]: undefined }));
    };

    const validate = (s) => {
        const e = {};
        if (s === 0 && !data.type) e.type = tx(lang, t.errType);
        if (s === 2) {
            if (!data.name.trim()) e.name = tx(lang, t.errName);
            if (!EMAIL_RE.test(data.email)) e.email = tx(lang, t.errEmail);
            if (!data.message.trim()) e.message = tx(lang, t.errMessage);
        }
        return e;
    };

    const next = () => {
        const e = validate(step);
        setErrors(e);
        if (Object.keys(e).length) return;
        setDir(1);
        setStep((s) => Math.min(2, s + 1));
    };

    const back = () => {
        setDir(-1);
        setErrors({});
        setStep((s) => Math.max(0, s - 1));
    };

    const submit = async () => {
        const e = validate(2);
        setErrors(e);
        if (Object.keys(e).length) return;
        setSending(true);
        try {
            const res = await fetch(`${API}/contact`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: data.name,
                    email: data.email,
                    company: data.company || null,
                    project_type: data.type,
                    budget: data.budget || null,
                    timeline: data.timeline || null,
                    message: data.message,
                }),
            });
            if (!res.ok) throw new Error("send failed");
            setDone(true);
            toast.success(tx(lang, t.successTitle), {
                duration: 3000,
                style: {
                    background: "#131318",
                    color: "#FAF9F6",
                    border: "1px solid rgba(138,43,226,0.6)",
                    borderRadius: 0,
                    fontFamily: "'JetBrains Mono', monospace",
                    letterSpacing: "0.15em",
                    fontSize: "11px",
                },
            });
        } catch {
            toast.error(tx(lang, t.errSend));
        } finally {
            setSending(false);
        }
    };

    const enhance = async () => {
        if (!data.message.trim()) {
            toast(tx(lang, t.aiEmpty));
            return;
        }
        setEnhancing(true);
        try {
            const res = await fetch(`${API}/enhance-message`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    message: data.message,
                    lang,
                    project_type: data.type,
                    budget: data.budget,
                    timeline: data.timeline,
                }),
            });
            if (!res.ok) throw new Error("enhance failed");
            const json = await res.json();
            if (!json.enhanced) throw new Error("empty");
            set("message")(json.enhanced);
            toast.success(tx(lang, t.aiDone), {
                duration: 2400,
                style: {
                    background: "#131318",
                    color: "#FAF9F6",
                    border: "1px solid rgba(138,43,226,0.6)",
                    borderRadius: 0,
                    fontFamily: "'JetBrains Mono', monospace",
                    letterSpacing: "0.15em",
                    fontSize: "11px",
                },
            });
        } catch {
            toast.error(tx(lang, t.aiFail));
        } finally {
            setEnhancing(false);
        }
    };

    const reset = () => {
        setData({ type: "", budget: "", timeline: "", name: "", email: "", company: "", message: "" });
        setErrors({});
        setStep(0);
        setDir(-1);
        setDone(false);
    };

    const stepTitles = [t.step1Title, t.step2Title, t.step3Title];

    return (
        <div
            data-testid="contact-form"
            className="border border-white/10 bg-ink2/70 p-6 backdrop-blur-sm md:p-9"
        >
            {done ? (
                <motion.div
                    data-testid="form-success"
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    className="flex min-h-[420px] flex-col items-center justify-center text-center"
                >
                    <span className="flex h-16 w-16 items-center justify-center rounded-full border border-violet bg-violet/15">
                        <Check size={26} className="text-violet" />
                    </span>
                    <h3 className="mt-8 font-display text-3xl font-extrabold uppercase tracking-tight text-cream md:text-4xl">
                        {tx(lang, t.successTitle)}
                    </h3>
                    <p className="mt-4 max-w-xs text-sm font-light text-cream/60">
                        {tx(lang, t.successBody)}
                    </p>
                    <button
                        data-testid="form-reset-btn"
                        onClick={reset}
                        className="mt-10 rounded-full border border-cream/30 px-8 py-4 font-mono text-[11px] tracking-[0.3em] text-cream transition-colors duration-300 hover:border-violet hover:text-violet"
                    >
                        {tx(lang, t.successReset)}
                    </button>
                </motion.div>
            ) : (
                <>
                    <div className="flex items-center justify-between">
                        <p className="font-mono text-[10px] tracking-[0.3em] text-violet">
                            {tx(lang, t.step)} {String(step + 1).padStart(2, "0")} {tx(lang, t.of)} 03
                        </p>
                        <p className="font-mono text-[10px] tracking-[0.3em] text-cream/40">
                            {tx(lang, t.title)}
                        </p>
                    </div>
                    <div className="mt-4 flex gap-2" data-testid="form-progress">
                        {[0, 1, 2].map((i) => (
                            <span
                                key={i}
                                className={`h-px flex-1 transition-colors duration-500 ${
                                    i <= step ? "bg-violet" : "bg-cream/15"
                                }`}
                            />
                        ))}
                    </div>

                    <div className="mt-8 min-h-[290px]">
                        <AnimatePresence mode="wait" custom={dir}>
                            <motion.div
                                key={step}
                                initial={{ x: 44 * dir, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                exit={{ x: -44 * dir, opacity: 0 }}
                                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                            >
                                <h3 className="font-display text-xl font-extrabold uppercase tracking-tight text-cream md:text-2xl">
                                    {tx(lang, stepTitles[step])}
                                </h3>

                                {step === 0 && (
                                    <div className="mt-7 flex flex-wrap gap-2.5">
                                        {FORM_TYPES.map((o) => (
                                            <Chip
                                                key={o.key}
                                                testid={`form-chip-${o.key}`}
                                                active={data.type === o.key}
                                                onClick={() => set("type")(o.key)}
                                            >
                                                {o[lang]}
                                            </Chip>
                                        ))}
                                        {errors.type && (
                                            <span data-testid="form-error-type" className="mt-2 block w-full font-mono text-[10px] tracking-[0.2em] text-blood">
                                                {errors.type}
                                            </span>
                                        )}
                                    </div>
                                )}

                                {step === 1 && (
                                    <div className="mt-7 space-y-8">
                                        <div>
                                            <p className="font-mono text-[10px] tracking-[0.3em] text-cream/50">
                                                {tx(lang, t.budget)}
                                            </p>
                                            <div className="mt-4 flex flex-wrap gap-2.5">
                                                {FORM_BUDGETS.map((o) => (
                                                    <Chip
                                                        key={o.key}
                                                        testid={`form-budget-${o.key}`}
                                                        active={data.budget === o.key}
                                                        onClick={() => set("budget")(o.key)}
                                                    >
                                                        {o[lang]}
                                                    </Chip>
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <p className="font-mono text-[10px] tracking-[0.3em] text-cream/50">
                                                {tx(lang, t.timeline)}
                                            </p>
                                            <div className="mt-4 flex flex-wrap gap-2.5">
                                                {FORM_TIMELINES.map((o) => (
                                                    <Chip
                                                        key={o.key}
                                                        testid={`form-timeline-${o.key}`}
                                                        active={data.timeline === o.key}
                                                        onClick={() => set("timeline")(o.key)}
                                                    >
                                                        {o[lang]}
                                                    </Chip>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {step === 2 && (
                                    <div className="mt-7 space-y-6">
                                        <div className="grid gap-6 sm:grid-cols-2">
                                            <Field label={tx(lang, t.name)} error={errors.name} testid="form-name">
                                                <input
                                                    data-testid="form-name-input"
                                                    value={data.name}
                                                    onChange={(e) => set("name")(e.target.value)}
                                                    placeholder={tx(lang, t.namePh)}
                                                    className="mt-2 w-full border-b border-cream/25 bg-transparent pb-2.5 font-light text-cream outline-none transition-colors placeholder:text-cream/25 focus:border-violet"
                                                />
                                            </Field>
                                            <Field label={tx(lang, t.email)} error={errors.email} testid="form-email">
                                                <input
                                                    data-testid="form-email-input"
                                                    type="email"
                                                    value={data.email}
                                                    onChange={(e) => set("email")(e.target.value)}
                                                    placeholder="you@mail.com"
                                                    className="mt-2 w-full border-b border-cream/25 bg-transparent pb-2.5 font-light text-cream outline-none transition-colors placeholder:text-cream/25 focus:border-violet"
                                                />
                                            </Field>
                                        </div>
                                        <Field label={tx(lang, t.company)} testid="form-company">
                                            <input
                                                data-testid="form-company-input"
                                                value={data.company}
                                                onChange={(e) => set("company")(e.target.value)}
                                                placeholder={tx(lang, t.companyPh)}
                                                className="mt-2 w-full border-b border-cream/25 bg-transparent pb-2.5 font-light text-cream outline-none transition-colors placeholder:text-cream/25 focus:border-violet"
                                            />
                                        </Field>
                                        <Field label={tx(lang, t.message)} error={errors.message} testid="form-message">
                                            <textarea
                                                data-testid="form-message-input"
                                                rows={3}
                                                value={data.message}
                                                onChange={(e) => set("message")(e.target.value)}
                                                placeholder={tx(lang, t.messagePh)}
                                                className="mt-2 w-full resize-none border-b border-cream/25 bg-transparent pb-2.5 font-light text-cream outline-none transition-colors placeholder:text-cream/25 focus:border-violet"
                                            />
                                            <button
                                                data-testid="form-ai-enhance"
                                                onClick={enhance}
                                                disabled={enhancing}
                                                className="mt-3 flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] text-violet transition-colors hover:text-cream disabled:opacity-50"
                                            >
                                                {enhancing ? (
                                                    <>
                                                        <Loader2 size={12} className="animate-spin" />
                                                        {tx(lang, t.aiWorking)}
                                                    </>
                                                ) : (
                                                    <>
                                                        <Wand2 size={12} />
                                                        {tx(lang, t.aiEnhance)}
                                                    </>
                                                )}
                                            </button>
                                        </Field>
                                    </div>
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    <div className="mt-8 flex items-center justify-between gap-4">
                        {step > 0 ? (
                            <button
                                data-testid={`form-back-btn-${step}`}
                                onClick={back}
                                className="flex items-center gap-2 font-mono text-[11px] tracking-[0.3em] text-cream/60 transition-colors hover:text-cream"
                            >
                                <ArrowLeft size={13} />
                                {tx(lang, t.back)}
                            </button>
                        ) : (
                            <span />
                        )}
                        {step < 2 ? (
                            <button
                                data-testid={`form-next-btn-${step}`}
                                onClick={next}
                                className="group flex items-center gap-3 rounded-full bg-cream px-7 py-3.5 font-mono text-[11px] tracking-[0.3em] text-ink transition-colors duration-300 hover:bg-violet hover:text-cream"
                            >
                                {tx(lang, t.next)}
                                <ArrowRight size={13} />
                            </button>
                        ) : (
                            <button
                                data-testid="form-submit-btn"
                                onClick={submit}
                                disabled={sending}
                                className="flex items-center gap-3 rounded-full bg-cream px-7 py-3.5 font-mono text-[11px] tracking-[0.3em] text-ink transition-colors duration-300 hover:bg-violet hover:text-cream disabled:opacity-60"
                            >
                                {sending ? (
                                    <>
                                        {tx(lang, t.sending)}
                                        <Loader2 size={13} className="animate-spin" />
                                    </>
                                ) : (
                                    <>
                                        {tx(lang, t.send)}
                                        <ArrowRight size={13} />
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
